// =====================================================================
// 🎨 RECETAS VISUALES DEL PANEL ADMIN
// Clases compartidas por todas las pantallas del panel, para que se vean
// como un solo sistema (misma familia que la vista del cliente: grafito en
// oscuro, porcelana en claro, ámbar como único acento). Solo estilos:
// ninguna pantalla depende de esto para su lógica.
// =====================================================================

export const getAdminTheme = (isDarkMode) => (isDarkMode ? {
  page: 'bg-gray-950 text-gray-50',
  card: 'bg-gray-850 border border-white/[0.07]',
  inset: 'bg-white/[0.04]',
  ink: 'text-gray-50',
  soft: 'text-gray-400',
  faint: 'text-gray-500',
  line: 'border-white/[0.08]',
  divide: 'divide-white/[0.06]',
  hover: 'hover:bg-white/[0.04]',
  track: 'bg-white/[0.06]',
  segOn: 'bg-gray-700 text-gray-50 shadow-[0_1px_2px_rgba(0,0,0,0.3)]',
  segOff: 'text-gray-400 hover:text-gray-50',
  btnSecondary: 'bg-white/[0.06] hover:bg-white/[0.1] text-gray-50',
  input: 'bg-gray-900 border border-white/[0.1] text-gray-50 placeholder:text-gray-500 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/15',
  chart: {
    grid: 'rgba(255,255,255,0.06)',
    axis: '#9C9FA2',
    surface: '#262421',
    border: 'rgba(255,255,255,0.1)',
    ink: '#F3F1EE',
    cursor: 'rgba(245,158,11,0.08)',
  },
} : {
  page: 'bg-gray-100 text-gray-900',
  card: 'bg-white border border-gray-900/[0.07]',
  inset: 'bg-gray-100',
  ink: 'text-gray-900',
  soft: 'text-gray-600',
  faint: 'text-gray-400',
  line: 'border-gray-900/[0.08]',
  divide: 'divide-gray-900/[0.06]',
  hover: 'hover:bg-gray-900/[0.03]',
  track: 'bg-gray-900/[0.05]',
  segOn: 'bg-white text-gray-900 shadow-[0_1px_3px_rgba(21,23,26,0.12)]',
  segOff: 'text-gray-600 hover:text-gray-900',
  btnSecondary: 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-900/10',
  input: 'bg-white border border-gray-900/10 text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/15',
  chart: {
    grid: 'rgba(21,23,26,0.06)',
    axis: '#77766F',
    surface: '#FFFFFF',
    border: 'rgba(21,23,26,0.1)',
    ink: '#15171A',
    cursor: 'rgba(245,158,11,0.1)',
  },
});

// Tipografía y piezas que no cambian con el tema
export const adType = {
  pageTitle: 'font-display text-[40px] md:text-[48px] leading-[0.95]',
  sectionTitle: 'text-[17px] font-bold',
  label: 'text-[13px] font-medium',
  number: 'font-display tabular-nums leading-none',
};

export const adButton = {
  primary: 'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-[14px] font-semibold transition-colors active:scale-[0.98]',
  base: 'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-[14px] font-semibold transition-colors active:scale-[0.98]',
};

// Colores de estado (turnos, caja): un tono de acento por estado, usado
// solo en puntos, íconos y números — nunca como fondo de toda la tarjeta.
export const adStatus = {
  indigo: { text: 'text-indigo-500', dot: 'bg-indigo-500', tint: 'bg-indigo-500/10' },
  green: { text: 'text-emerald-500', dot: 'bg-emerald-500', tint: 'bg-emerald-500/10' },
  blue: { text: 'text-blue-500', dot: 'bg-blue-500', tint: 'bg-blue-500/10' },
  yellow: { text: 'text-amber-500', dot: 'bg-amber-500', tint: 'bg-amber-500/10' },
  gray: { text: 'text-gray-400', dot: 'bg-gray-400', tint: 'bg-gray-400/10' },
  red: { text: 'text-rose-500', dot: 'bg-rose-500', tint: 'bg-rose-500/10' },
};

export const GOLD = '#C9A23A'; // membresías (igual que en la vista del cliente)

// Campos de formulario (modales de alta/edición). 16px en el texto para
// que iOS no haga zoom al enfocar.
export const getAdminForm = (isDarkMode) => {
  const t = getAdminTheme(isDarkMode);
  const base = `w-full rounded-xl px-4 text-[16px] font-medium outline-none transition-[border-color,box-shadow] ${t.input}`;
  return {
    label: `block mb-1.5 ${adType.label} ${t.soft}`,
    hint: `text-[13px] leading-relaxed ${t.soft}`,
    field: `${base} h-12`,
    textarea: `${base} py-3 resize-none`,
    select: `${base} h-12 appearance-none pr-10`,
  };
};
